import { useState } from 'react'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { AuthShell } from './AuthShell'
import type { TotpSetupState, TotpVerifyState } from './TotpPage'

/**
 * Forma qiymatlari uchun sxema.
 *
 * antd `rules` FOYDALANUVCHIGA xabar ko'rsatadi, zod esa TIPNI
 * kafolatlaydi — ikkalasi ikki xil ish qiladi.
 */
const loginSchema = z.object({
  username: z.string().trim().min(1),
  password: z.string().min(1),
})

type LoginFormValues = z.infer<typeof loginSchema>

interface LocationState {
  from?: { pathname?: string }
}

/**
 * Sherikning kirish ekrani — birinchi bosqich.
 *
 * ⚠️ KIRISH BU YERDA TUGAMAYDI. `PartnerAuthResource` xodim bilan AYNAN
 * bir xil `LoginFlowService` ni ishlatadi, ya'ni TOTP sherik uchun ham
 * MAJBURIY va natija doim `/login/totp` ga olib boradi.
 *
 * ⚠️ «Xodim bu yerga kirmaydi» eslatmasi EKRANDA turadi. Ajratmani
 * server majburlaydi (ADR-029 §4: `UserKind.PARTNER`), lekin rad javobi
 * «login yoki parol noto'g'ri» bo'lib ko'rinadi — xodim parolini uch
 * marta qayta terardi. Sabab oldindan aytilsa, o'sha uch urinish umuman
 * bo'lmaydi.
 */
export function LoginPage() {
  const { t } = useTranslation()
  const { isAuthenticated, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Kirgan foydalanuvchi login sahifasini ochsa — orqaga qaytariladi
  if (isAuthenticated) return <Navigate to="/" replace />

  const handleFinish = (values: unknown) => {
    const parsed = loginSchema.safeParse(values)
    if (!parsed.success) return

    setSubmitting(true)
    setError(null)

    const state = location.state as LocationState | null
    const destination = state?.from?.pathname ?? '/'

    void signIn(parsed.data.username, parsed.data.password)
      .then((outcome) => {
        if (outcome.kind === 'totpSetup') {
          const next: TotpSetupState = {
            stage: 'setup',
            setupToken: outcome.setupToken,
            qrUri: outcome.qrUri,
            secret: outcome.secret,
            from: destination,
          }
          navigate('/login/totp', { state: next })
          return
        }
        const next: TotpVerifyState = {
          stage: 'verify',
          totpToken: outcome.totpToken,
          from: destination,
        }
        navigate('/login/totp', { state: next })
      })
      .catch((err: unknown) => setError(translateError(err, t)))
      .finally(() => setSubmitting(false))
  }

  return (
    <AuthShell title={t('auth.title')} subtitle={t('auth.subtitle')}>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

      <Form<LoginFormValues>
        layout="vertical"
        requiredMark={false}
        onFinish={handleFinish}
        disabled={submitting}
      >
        <Form.Item
          name="username"
          label={t('auth.username')}
          rules={[{ required: true, message: t('auth.username') }]}
        >
          <Input prefix={<UserOutlined />} autoFocus autoComplete="username" />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('auth.password')}
          rules={[{ required: true, message: t('auth.password') }]}
        >
          <Input.Password prefix={<LockOutlined />} autoComplete="current-password" />
        </Form.Item>

        <Button type="primary" htmlType="submit" block loading={submitting}>
          {submitting ? t('auth.signingIn') : t('auth.signIn')}
        </Button>
      </Form>

      <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
        {t('auth.staffHint')}
      </Typography.Paragraph>
    </AuthShell>
  )
}
